import React, { useState, useRef } from 'react';
import { formatDistanceToNow, format } from 'date-fns';
import { useAuth } from '@clerk/clerk-react';
import { ApiClient } from '../utils/apiClient';

const ConversationDetails = ({ conversation, onClose, onStatusChange, onDelete }) => {
  const { getToken } = useAuth();
  const apiClient = useRef(new ApiClient(getToken)).current;
  const [isPlaying, setIsPlaying] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(null);

  // Format duration from seconds to MM:SS
  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Format phone number for display
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return 'Unknown';
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      const number = cleaned.slice(1);
      return `+1 (${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`;
    }
    return phoneNumber;
  };

  // Get status badge styling
  const getStatusBadge = (status) => {
    const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
    switch (status) {
      case 'completed':
        return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'active':
      case 'connected':
      case 'in-progress':
      case 'connecting':
        return `${baseClasses} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`;
      case 'failed':
      case 'initiated':
        return `${baseClasses} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200`;
    }
  };

  // Handle audio playback
  const handlePlayRecording = async () => {
    if (!conversation.elevenLabsData?.recordingUrl) {
      alert('Recording not available');
      return;
    }

    try {
      if (isPlaying) {
        audioRef.current?.pause();
        setIsPlaying(false);
      } else {
        if (audioRef.current) {
          audioRef.current.src = conversation.elevenLabsData.recordingUrl;
          await audioRef.current.play();
          setIsPlaying(true);
        }
      }
    } catch (error) {
      console.error('Failed to play recording:', error);
      alert('Failed to play recording. Please try again.');
    }
  };

  // Handle audio download
  const handleDownloadRecording = async () => {
    if (!conversation.elevenLabsData?.recordingUrl) {
      alert('Recording not available');
      return;
    }

    try {
      const link = document.createElement('a');
      link.href = conversation.elevenLabsData.recordingUrl;
      link.download = `conversation-${conversation.id}-${format(new Date(conversation.startTime), 'yyyy-MM-dd-HH-mm')}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to download recording:', error);
      alert('Failed to download recording. Please try again.');
    }
  };

  // Handle transcript download
  const handleDownloadTranscript = () => {
    if (!conversation.elevenLabsData?.transcript || conversation.elevenLabsData.transcript.length === 0) {
      alert('Transcript not available');
      return;
    }

    try {
      const transcriptText = conversation.elevenLabsData.transcript
        .map(message => `[${message.timestamp ? format(new Date(message.timestamp), 'HH:mm:ss') : 'Unknown'}] ${message.speaker}: ${message.text}`)
        .join('\n\n');

      const blob = new Blob([transcriptText], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `conversation-${conversation.id}-${format(new Date(conversation.startTime), 'yyyy-MM-dd-HH-mm')}-transcript.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download transcript:', error);
      alert('Failed to download transcript. Please try again.');
    }
  };

  // Handle mark as unread
  const handleMarkAsUnread = async () => {
    setLoading(true);
    try {
      // Mark conversation as unread by clearing the last_viewed_at timestamp
      const response = await apiClient.put(`/api/conversations/${conversation.id}/unread`);

      if (response.success) {
        // Update local conversation state
        conversation.last_viewed_at = null;
      }
    } catch (error) {
      console.error('Failed to mark as unread:', error);
      alert('Failed to mark conversation as unread. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    setLoading(true);
    try {
      await onDelete(conversation.id);
      onClose();
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      alert('Failed to delete conversation. Please try again.');
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white dark:bg-gray-800">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Conversation Details
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 dark:text-gray-300 dark:text-gray-600"
          >
            <span className="sr-only">Close</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="mt-4 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600">
                  Parrot Name
                </label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">
                  {conversation.parrotName || 'Unknown Parrot'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600">
                  Phone Number
                </label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">
                  {formatPhoneNumber(conversation.phoneNumber)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600">
                  Caller Name
                </label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">
                  {conversation.callerName || 'Unknown'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600">
                  Caller Phone
                </label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">
                  {formatPhoneNumber(conversation.callerNumber)}
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600">
                  Date & Time
                </label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">
                  {conversation.startTime ? format(new Date(conversation.startTime), 'PPpp') : 'Unknown'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {conversation.startTime ? formatDistanceToNow(new Date(conversation.startTime), { addSuffix: true }) : ''}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600">
                  Duration
                </label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">
                  {formatDuration(conversation.duration)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600">
                  Status
                </label>
                <div className="mt-1">
                  <span className={getStatusBadge(conversation.status)}>
                    {conversation.status}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600">
                  Call Status
                </label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">
                  {conversation.callStatus || 'Unknown'}
                </p>
                {conversation.failureReason && (
                  <p className="text-xs text-red-600 dark:text-red-400">
                    Reason: {conversation.failureReason}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Recording Controls */}
          {conversation.elevenLabsData?.recordingUrl && (
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg dark:bg-gray-900">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                Call Recording
              </h4>
              <div className="flex space-x-3">
                <button
                  onClick={handlePlayRecording}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 dark:text-white"
                >
                  <span className="mr-2">{isPlaying ? '⏸️' : '▶️'}</span>
                  {isPlaying ? 'Pause' : 'Play'}
                </button>
                <button
                  onClick={handleDownloadRecording}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 dark:bg-gray-900 dark:text-gray-600"
                >
                  <span className="mr-2">⬇️</span>
                  Download
                </button>
              </div>
              <audio
                ref={audioRef}
                onEnded={() => setIsPlaying(false)}
                onError={() => {
                  setIsPlaying(false);
                  alert('Failed to load recording');
                }}
                className="hidden"
              />
            </div>
          )}

          {/* Call Summary */}
          {conversation.elevenLabsData?.callSummary && (
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                Call Summary
              </h4>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg dark:bg-gray-900">
                <p className="text-sm text-gray-700 dark:text-gray-300 dark:text-gray-600">
                  {conversation.elevenLabsData.callSummary}
                </p>
              </div>
            </div>
          )}

          {/* Transcript */}
          {conversation.elevenLabsData?.transcript && conversation.elevenLabsData.transcript.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                  Conversation Transcript
                </h4>
                <button
                  onClick={handleDownloadTranscript}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 dark:bg-gray-900 dark:text-gray-600"
                >
                  <span className="mr-1">⬇️</span>
                  Download
                </button>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg max-h-64 overflow-y-auto dark:bg-gray-900">
                <div className="space-y-3">
                  {conversation.elevenLabsData.transcript.map((message, index) => (
                    <div key={index} className="flex space-x-3">
                      <div className="flex-shrink-0">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          message.speaker === 'agent' 
                            ? 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200'
                            : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        }`}>
                          {message.speaker === 'agent' ? '🦜' : '👤'} {message.speaker}
                        </span>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-700 dark:text-gray-300 dark:text-gray-600">
                          {message.text}
                        </p>
                        {message.timestamp && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {format(new Date(message.timestamp), 'HH:mm:ss')}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            {/* Show Mark as Unread button only for completed and failed conversations */}
            {['completed', 'failed'].includes(conversation.status) && (
              <div className="flex space-x-3">
                <button
                  onClick={handleMarkAsUnread}
                  disabled={loading}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 dark:text-white"
                >
                  <span className="mr-2">📧</span>
                  Mark as Unread
                </button>
              </div>
            )}
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={loading}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 dark:text-white"
              >
                Delete
              </button>
              <button
                onClick={onClose}
                className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 dark:bg-gray-900 dark:text-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-60">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
              <div className="mt-3 text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900">
                  <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mt-2">
                  Delete Conversation
                </h3>
                <div className="mt-2 px-7 py-3">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Are you sure you want to delete this conversation? This action will hide it from your view but preserve the data for system records.
                  </p>
                </div>
                <div className="items-center px-4 py-3">
                  <div className="flex space-x-3 justify-center">
                    <button
                      onClick={handleDeleteConfirm}
                      disabled={loading}
                      className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 dark:text-white"
                    >
                      {loading ? 'Deleting...' : 'Delete'}
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={loading}
                      className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 dark:bg-gray-900 dark:text-gray-600"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationDetails;
